import React from 'react';
import { Box, Flex, IconButton, VisuallyHidden } from '@chakra-ui/react';
import type { SystemStyleObject } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ARROW_PX,
  ARROW_TAP_EXTENSION_X_PX,
  ARROW_TAP_EXTENSION_Y_PX,
  BAR_PADDING_PX,
  DOT_GAP_PX,
  DOT_PX,
  FLOATING_GAP_PX,
  INLINE_GAP_PX,
} from './photoBarGeometry';

/**
 * Arrow tap target: the visible button is Chakra `xs` (24px); this ::after
 * stretches the hit area to 44x44 (WCAG 2.5.5) without changing the look.
 * The bar sits 4px above the photo's bottom edge (+4px of bar padding, so
 * the arrow's bottom is 8px above the edge) and the photo/hero clips at that
 * edge, so the extension is asymmetric — 12px up, 8px down (stops AT the
 * edge, never onto content below the photo), 10px to each side.
 */
const ARROW_HIT_AREA: SystemStyleObject = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: `-12px -${ARROW_TAP_EXTENSION_X_PX}px -8px -${ARROW_TAP_EXTENSION_X_PX}px`,
  },
};

/**
 * `inline` layout (the hero's strip, which now floats 16px above the photo's
 * bottom edge): there is room below, so the tap area is symmetric, 10px above
 * and below (24 + 20 = 44px). Measured from the photo's edge it now reaches
 * higher than the old 12px-up / 8px-down area (the strip itself is 12px higher),
 * leaving 6px of clearance to the stats row at 320 and 14px at lg.
 */
const ARROW_HIT_AREA_INLINE: SystemStyleObject = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: `-${ARROW_TAP_EXTENSION_Y_PX}px -${ARROW_TAP_EXTENSION_X_PX}px -${ARROW_TAP_EXTENSION_Y_PX}px -${ARROW_TAP_EXTENSION_X_PX}px`,
  },
};

interface ImageNavigationProps {
  currentIndex: number;
  totalImages: number;
  onPrevious: () => void;
  onNext: () => void;
  preventPropagation?: boolean;
  /**
   * `floating` (default): the bar positions itself, centred over the bottom of
   * its positioned parent. `inline`: the bar is a plain flex row the caller
   * places (e.g. in a grid cell next to the hero's Photos pill).
   */
  layout?: 'floating' | 'inline';
  /**
   * Bounds the bar's width. Above this many photos the dots are replaced by a
   * compact "n / N" counter. Default: unbounded (always dots).
   */
  maxDots?: number;
  /**
   * Viewport width (px) below which the dots are replaced by the counter,
   * for callers that share the row with another control (the Photos pill).
   * Pure CSS (media query), so there is no resize listener or hop.
   */
  dotsMinViewportPx?: number;
}

export const ImageNavigation: React.FC<ImageNavigationProps> = ({
  currentIndex,
  totalImages,
  onPrevious,
  onNext,
  preventPropagation = true,
  layout = 'floating',
  maxDots = Infinity,
  dotsMinViewportPx,
}) => {
  if (totalImages <= 1) return null;

  // Counter replaces the dots when there are too many for the bar to stay
  // narrow (always), or on viewports too narrow to share the row (CSS only).
  const alwaysCounter = totalImages > maxDots;
  const narrowQuery = dotsMinViewportPx ? `@media (max-width: ${dotsMinViewportPx - 0.02}px)` : undefined;

  const handleClick = (e: React.MouseEvent, callback: () => void) => {
    if (preventPropagation) {
      e.stopPropagation();
    }
    callback();
  };

  return (
    <Flex
      {...(layout === 'floating'
        ? { position: 'absolute' as const, bottom: 1, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }
        : { position: 'relative' as const })}
      gap={`${layout === 'inline' ? INLINE_GAP_PX : FLOATING_GAP_PX}px`}
      bg={'blackAlpha.600'}
      p={`${BAR_PADDING_PX}px`}
      borderRadius="full"
      // Dots are non-interactive: let touches on the bar (outside the arrows) fall through to the swipe area.
      pointerEvents="none"
    >
      <IconButton
        aria-label="Previous image"
        size="xs"
        minW={`${ARROW_PX}px`}
        h={`${ARROW_PX}px`}
        // Intentionally literal: control sits on a fixed photo-overlay scrim, must stay legible over variable photo content regardless of app mode
        bg="white"
        // Dark chevron set explicitly: the icon otherwise inherits the app's
        // text color, which is near-white in dark mode (white on white).
        color="gray.900"
        opacity={0.95}
        _hover={{ opacity: 1, bg: 'white' }}
        // pointerEvents none when disabled: a swipe that starts on the dead arrow reaches the carousel.
        _disabled={{ opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' }}
        pointerEvents="auto"
        sx={layout === 'inline' ? ARROW_HIT_AREA_INLINE : ARROW_HIT_AREA}
        borderRadius="full"
        isDisabled={currentIndex === 0}
        onClick={(e) => handleClick(e, onPrevious)}
      >
        <ChevronLeft size={16} />
      </IconButton>

      {/* Position indicator only: decorative dots/counter plus a polite text
          equivalent for screen readers. Swipe and the two arrows are the
          controls (dots are not tappable). */}
      {/* Dots: hidden below `dotsMinViewportPx`, or entirely above `maxDots`. */}
      {!alwaysCounter && (
        <Flex
          gap={`${DOT_GAP_PX}px`}
          align="center"
          aria-hidden="true"
          sx={narrowQuery ? { [narrowQuery]: { display: 'none' } } : undefined}
        >
          {Array.from({ length: totalImages }, (_, index) => (
            <Box
              key={index}
              w={`${DOT_PX}px`}
              h={`${DOT_PX}px`}
              borderRadius="50%"
              // Intentionally literal: pagination dot sits on a fixed photo-overlay scrim, must stay legible over variable photo content regardless of app mode
              bg={index === currentIndex ? "white" : "var(--color-white-overlay-50)"}
            />
          ))}
        </Flex>
      )}
      {/* Counter: always when there are too many dots; otherwise only on narrow viewports. */}
      {(alwaysCounter || narrowQuery) && (
        <Box
          aria-hidden="true"
          alignSelf="center"
          fontSize="xs"
          fontWeight={600}
          lineHeight={1}
          whiteSpace="nowrap"
          // Near-white token (98%): sits on the fixed photo-overlay scrim like the dots.
          color="var(--color-white-overlay-98)"
          display={alwaysCounter ? 'block' : 'none'}
          sx={!alwaysCounter && narrowQuery ? { [narrowQuery]: { display: 'block' } } : undefined}
        >
          {`${currentIndex + 1} / ${totalImages}`}
        </Box>
      )}
      <VisuallyHidden role="status">
        {`Photo ${currentIndex + 1} of ${totalImages}`}
      </VisuallyHidden>

      <IconButton
        aria-label="Next image"
        size="xs"
        minW={`${ARROW_PX}px`}
        h={`${ARROW_PX}px`}
        // Intentionally literal: control sits on a fixed photo-overlay scrim, must stay legible over variable photo content regardless of app mode
        bg="white"
        // Dark chevron set explicitly: the icon otherwise inherits the app's
        // text color, which is near-white in dark mode (white on white).
        color="gray.900"
        opacity={0.95}
        _hover={{ opacity: 1, bg: 'white' }}
        // pointerEvents none when disabled: a swipe that starts on the dead arrow reaches the carousel.
        _disabled={{ opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' }}
        pointerEvents="auto"
        sx={layout === 'inline' ? ARROW_HIT_AREA_INLINE : ARROW_HIT_AREA}
        borderRadius="full"
        isDisabled={currentIndex === totalImages - 1}
        onClick={(e) => handleClick(e, onNext)}
      >
        <ChevronRight size={16} />
      </IconButton>
    </Flex>
  );
};